import React, { Suspense, useEffect, useLayoutEffect, useState } from 'react';
import { Container, Row, Col } from 'react-grid-system';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import ProjectIndex from './ProjectIndex';
import '../App.css';

const Viewport3D = React.lazy(() => import('./Viewport3D'));

const MediaEl = ({ src, alt }) =>
  src
    ? src.endsWith('.mp4')
      ? <video src={src} autoPlay loop muted playsInline preload="auto" style={{ width: '100%', display: 'block' }} />
      : <img src={src} alt={alt || ''} style={{ width: '100%', display: 'block' }} />
    : null;

function renderSection(section, i) {
  const ratio = section.ratio || '16 / 9';
  const sep = i === 0 ? '' : 'cs-section-sep';
  const sepStyle = section.marginTop !== undefined ? { marginTop: section.marginTop } : undefined;

  if (section.type === 'full') {
    // cols: 6 = full content width (default), cols: 3 = half, etc.
    const widthPct = section.cols ? `${(section.cols / 6) * 100}%` : '100%';
    return (
      <div key={i} className={sep} style={sepStyle}>
        <div className={section.cols && section.cols < 6 ? 'cs-full-partial' : undefined} style={{ width: widthPct, margin: section.centered ? '0 auto' : undefined }}>
          <MediaEl src={section.src} alt={section.caption} />
        </div>
        {section.caption && <div className={`project-caption-black${section.cols && section.cols < 6 ? ' cs-full-partial' : ''}`} style={{ width: widthPct, margin: section.centered ? '0 auto' : undefined }}>{section.caption}</div>}
      </div>
    );
  }

  if (section.type === 'half') {
    return (
      <div key={i} className={sep} style={sepStyle}>
        <div className={`cs-half-images${section.tight ? ' cs-half-tight' : ''}`}>
          {section.images.map((img, j) => (
            <div key={j}>
              <MediaEl src={img.src} alt={img.caption} />
              {img.caption && <div className="project-caption-black">{img.caption}</div>}
            </div>
          ))}
        </div>
        {section.caption && <div className="project-caption-black" style={{ marginTop: 8 }}>{section.caption}</div>}
      </div>
    );
  }

  if (section.type === 'third') {
    const perRow = section.cols || 3;
    const colSpan = 12 / perRow;
    const rowGap = section.rowGap || '100px';
    const rows = [];
    for (let j = 0; j < section.images.length; j += perRow) rows.push(section.images.slice(j, j + perRow));
    return (
      <div key={i} className={sep}>
        {rows.map((row, ri) => (
          <Row key={ri} style={{ marginTop: ri > 0 ? rowGap : 0 }}>
            {row.map((img, j) => (
              <Col sm={colSpan} key={j}>
                <MediaEl src={img.src} alt={img.caption} />
                {img.caption && <div className="project-caption-black">{img.caption}</div>}
              </Col>
            ))}
          </Row>
        ))}
        {section.caption && <div className="project-caption-black">{section.caption}</div>}
      </div>
    );
  }

  if (section.type === 'video-half') {
    return (
      <div key={i} className={sep}>
        <div className="cs-half-images">
          {section.videos.map((vid, j) => (
            <div key={j}>
              <video src={vid.src} autoPlay loop muted playsInline style={{ width: '100%', display: 'block' }} />
              {vid.caption && <div className="project-caption-black">{vid.caption}</div>}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (section.type === 'narrative') {
    return (
      <div key={i} className={sep}>
        {section.label && <div className="narrative-label">{section.label}</div>}
        <div className="narrative-body">{section.body}</div>
      </div>
    );
  }

  if (section.type === 'type-specimen') {
    const specimenFont = section.fontFamily || 'inherit';
    return (
      <div key={i} className={sep}>
        {section.entries.map((entry, ei) => {
          const uid = `ts-${i}-${ei}`;
          const fs = entry.fontSize || '16px';
          return (
          <div key={ei} style={{ display: 'flex', marginTop: ei > 0 ? '48px' : 0 }}>
            <style>{`.${uid} { font-size: ${fs} !important; }`}</style>
            {/* Specimen — 75% */}
            <div style={{ flex: '0 0 75%', minWidth: 0 }}>
              {entry.text && (
                <div
                  className={uid}
                  style={{
                    fontFamily: specimenFont,
                    textTransform: entry.textTransform,
                    letterSpacing: entry.letterSpacing,
                    lineHeight: 1.1,
                  }}
                >
                  {entry.text}
                </div>
              )}
              {entry.weights && entry.weights.map((w, wi) => (
                <div key={wi} className={uid} style={{ fontFamily: specimenFont, marginBottom: '0.7em' }}>
                  <span className="type-specimen-weight" style={{ '--ts-weight': w.weight, '--ts-style': 'normal' }}>{w.label} </span>
                  <span className="type-specimen-weight" style={{ '--ts-weight': w.weight, '--ts-style': 'italic' }}>Italic </span>
                  <span className="type-specimen-weight" style={{ '--ts-weight': w.weight, '--ts-style': 'normal' }}>Aabbcc 123</span>
                </div>
              ))}
              {entry.meta && <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '10px', color: '#444', marginTop: '8px' }}>{entry.meta}</div>}
            </div>
            {/* Annotation — 25% */}
            <div style={{ flex: '0 0 25%', paddingLeft: '16px' }}>
              {entry.annotation && (
                <>
                  <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '11px', fontWeight: 'bold', marginBottom: '6px' }}>{entry.annotation.label}</div>
                  <div style={{ fontFamily: 'Arial, sans-serif', fontSize: '10px', lineHeight: 1.6, color: '#333' }}>{entry.annotation.body}</div>
                </>
              )}
            </div>
          </div>
          );
        })}
      </div>
    );
  }

  if (section.type === 'color-palette') {
    const perRow = section.perRow || 4;
    return (
      <div key={i} className={sep} data-nav-ignore="true">
        {section.label && <div className="project-caption-black" style={{ marginBottom: '8px' }}>{section.label}</div>}
        <div className="cs-color-palette" style={{ gridTemplateColumns: `repeat(${perRow}, 1fr)` }}>
          {section.colors.map((c, ci) => <ColorSwatch key={ci} c={c} />)}
        </div>
      </div>
    );
  }

  if (section.type === 'hover-grid') {
    const cols = section.cols || 4;
    return (
      <div key={i} className={sep}>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: section.gap || 0 }}>
          {section.items.map((item, j) => (
            <HoverGridItem key={j} item={item} />
          ))}
        </div>
        {section.caption && <div className="project-caption-black">{section.caption}</div>}
      </div>
    );
  }

  if (section.type === 'tabbed-palette') {
    return <TabbedPalette key={i} className={sep} section={section} navIgnore />;
  }

  if (section.type === 'motifs-scatter') {
    return <MotifsScatter key={i} className={sep} section={section} />;
  }

  return null;
}

function HoverGridItem({ item }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{ position: 'relative', aspectRatio: '1 / 1', overflow: 'hidden', cursor: 'default' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <img src={item.src} alt={item.alt || ''} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      {item.hoverSrc && (
        <img src={item.hoverSrc} alt={item.alt || ''} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block', opacity: hovered ? 1 : 0, transition: 'opacity 0.3s ease' }} />
      )}
    </div>
  );
}

function hexLum(hex) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

function ColorSwatch({ c }) {
  const bg = c.hex.startsWith('#') ? c.hex : `#${c.hex}`;
  const lum = hexLum(bg);
  const fg = (c.dark || lum > 130) ? 'rgba(0,0,0,0.75)' : 'rgba(255,255,255,0.85)';
  const boxShadow = lum > 240 ? 'inset 0 0 0 1px #e0e0e0' : 'none';
  return (
    <div style={{ flex: c.span || 1, gridColumn: c.span ? `span ${c.span}` : undefined, backgroundColor: bg, boxShadow, padding: '14px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', boxSizing: 'border-box', overflow: 'hidden' }}>
      <div style={{ fontSize: '11px', color: fg, marginBottom: '10px' }}>{c.name}</div>
      {c.pantone && c.pantone !== '—' && <div style={{ fontSize: '10px', color: fg }}>{c.pantone}</div>}
      {c.cmyk && c.cmyk !== '—' && <div style={{ fontSize: '10px', color: fg }}>{c.cmyk}</div>}
      {c.rgb && c.rgb !== '—' && <div style={{ fontSize: '10px', color: fg }}>{c.rgb}</div>}
      <div style={{ fontSize: '10px', color: fg }}>{c.hex}</div>
    </div>
  );
}

function PaletteTab({ label, active, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '11px', fontFamily: 'inherit', color: active ? '#000' : hovered ? '#555' : '#aaa', transition: 'color 0.3s ease' }}
    >
      {label}
    </button>
  );
}

function TabbedPalette({ section, className, navIgnore }) {
  const [activeTab, setActiveTab] = useState(0);
  const tab = section.tabs[activeTab];
  return (
    <div className={className} data-nav-ignore={navIgnore ? 'true' : undefined}>
      <div style={{ display: 'flex', gap: '24px', marginBottom: '12px' }}>
        {section.tabs.map((t, i) => (
          <PaletteTab key={i} label={t.label} active={i === activeTab} onClick={() => setActiveTab(i)} />
        ))}
      </div>
      <div className="cs-palette-row" style={{ display: 'flex', width: '75%' }}>
        {tab.colors.map((c, ci) => <ColorSwatch key={`${activeTab}-${ci}`} c={c} />)}
      </div>
      {section.shared && (() => {
        const perRow = section.sharedPerRow || 4;
        const rows = [];
        for (let j = 0; j < section.shared.length; j += perRow) rows.push(section.shared.slice(j, j + perRow));
        return (
          <>
            <div style={{ marginTop: '40px', marginBottom: '12px', fontSize: '11px', fontFamily: 'inherit', color: '#000' }}>Recurring</div>
            {rows.map((row, ri) => {
              const rowSpan = row.reduce((s, c) => s + (c.span || 1), 0);
              return (
                <div key={ri} className="cs-palette-row" style={{ display: 'flex', width: rowSpan < perRow ? `${(rowSpan / perRow) * 100}%` : '100%' }}>
                  {row.map((c, ci) => <ColorSwatch key={ci} c={c} />)}
                </div>
              );
            })}
          </>
        );
      })()}
    </div>
  );
}

function HoverFig({ label, src, srcColor, left, top, width, labelLeft, labelTop }) {
  const [hovered, setHovered] = useState(false);
  const hasExplicitLabel = labelLeft !== undefined || labelTop !== undefined;
  return (
    <>
      {hasExplicitLabel && (
        <div style={{ position: 'absolute', left: labelLeft, top: labelTop, fontSize: 'clamp(8px, 0.9vw, 13px)', fontFamily: 'inherit', color: '#222', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
          {label}
        </div>
      )}
      <div
        style={{ position: 'absolute', left, top, width }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {!hasExplicitLabel && (
          <div style={{ fontSize: 'clamp(8px, 0.9vw, 13px)', fontFamily: 'inherit', color: '#222', marginBottom: '0.3em', whiteSpace: 'nowrap' }}>{label}</div>
        )}
        <div style={{ position: 'relative' }}>
          <img src={src} alt={label} style={{ display: 'block', width: '100%' }} />
          {srcColor && (
            <img src={srcColor} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block', opacity: hovered ? 1 : 0, transition: 'opacity 0.35s ease' }} />
          )}
        </div>
      </div>
    </>
  );
}

function MotifsScatter({ section, className }) {
  return (
    <div className={className} style={{ position: 'relative', width: '100%', paddingBottom: '54%' }}>
      <div style={{ position: 'absolute', inset: 0 }}>
        <div style={{ position: 'absolute', top: '2%', left: '2%', fontStyle: 'italic', fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 'clamp(11px, 1.4vw, 20px)', color: '#222' }}>
          {section.title}
        </div>
        {section.items.map((item, j) => <HoverFig key={j} {...item} />)}
        {section.caption && (
          <div style={{ position: 'absolute', bottom: '4%', left: '2%', right: '5%', fontSize: 'clamp(10px, 1.1vw, 16px)', color: '#222' }}>
            {section.caption}
          </div>
        )}
      </div>
    </div>
  );
}

function Credits({ m }) {
  if (m.contributorGroups && m.contributorGroups.length > 0) {
    return m.contributorGroups.map((group, gi) => (
      <div key={gi} style={{ marginTop: gi > 0 ? '1.5em' : 0 }}>
        {group.members.length === 1 ? (
          <div className="subhead-black" style={{ paddingBottom: 0, textTransform: 'none' }}>
            <span>{group.label} </span>
            {group.members[0].link ? <a href={group.members[0].link}>{group.members[0].name || group.members[0].role}</a> : (group.members[0].name || group.members[0].role)}
          </div>
        ) : (
          <>
            <div className="subhead-black" style={{ paddingBottom: 0, textTransform: 'none', color: '#999' }}>{group.label}</div>
            {group.members.map((c, ci) => (
              <div className="subhead-black" key={ci} style={{ paddingBottom: 0, textTransform: 'none' }}>
                {c.role && c.name ? <>{c.role}, <a href={c.link}>{c.name}</a></> : c.role ? c.role : <a href={c.link}>{c.name}</a>}
              </div>
            ))}
          </>
        )}
      </div>
    ));
  }
  if (m.contributors && m.contributors.length > 0) {
    return m.contributors.map((c, ci) => (
      <div className="subhead-black" key={ci} style={{ paddingBottom: 0, textTransform: 'none' }}>
        {c.role && c.name ? <>{c.role}, <a href={c.link}>{c.name}</a></> : c.role ? c.role : <a href={c.link}>{c.name}</a>}
      </div>
    ));
  }
  return (
    <>
      {m.contributorRole && m.contributorRole.length > 0 && (
        <div className="subhead-black" style={{ paddingBottom: 0, textTransform: 'none' }}>
          {m.contributorRole}, <a href={m.contributorLink}>{m.contributorName}</a>
        </div>
      )}
      {m.contributorRole1 && m.contributorRole1.length > 0 && (
        <div className="subhead-black" style={{ paddingBottom: 0, textTransform: 'none' }}>
          {m.contributorRole1}, <a href={m.contributorLink1}>{m.contributorName1}</a>
        </div>
      )}
    </>
  );
}

function CaseStudyPage({ projectModel, dispatch }) {
  const m = projectModel;

  useLayoutEffect(() => {
    document.documentElement.style.backgroundColor = '#ffffff';
    document.body.style.backgroundColor = '#ffffff';
    document.documentElement.classList.add('page-light');

    return () => {
      document.documentElement.classList.remove('page-light');
      document.documentElement.style.backgroundColor = '';
      document.body.style.backgroundColor = '';
    };
  }, []);  // eslint-disable-line

  useEffect(() => {
    let rafId = null;
    const update = () => {
      const scrollBottom = window.scrollY + window.innerHeight;
      const docHeight = document.documentElement.scrollHeight;
      // Switch to black when within ~300px of the bottom (footer zone)
      const inFooter = scrollBottom > docHeight - 300;
      const bg = inFooter ? '#e0e0e0' : '#ffffff';
      document.documentElement.style.backgroundColor = bg;
      document.body.style.backgroundColor = bg;
    };
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => { rafId = null; update(); });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);  // eslint-disable-line

  return (
    <div className="wrapper" style={{ backgroundColor: '#ffffff' }}>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Maria Chimishkyan — {m.title}</title>
      </Helmet>

      {/* Cover — dark background so nav samples black when cover is present */}
      {(m.coverVideo || m.coverImage) && (
        <div style={{ backgroundColor: '#000' }}>
          {m.coverVideo
            ? <video className="cover" loop autoPlay muted playsInline style={{ objectFit: 'cover' }} src={m.coverVideo} />
            : <img className="cover" src={m.coverImage} alt={m.title} style={{ objectFit: 'cover', width: '100%' }} />}
        </div>
      )}

      {/* Main content — same structure as project pages */}
      <div className="inner-wrapper">
        <Container fluid className="page-pad">
          <Row>

            {/* Description — Col lg={3}: meta always first, credits hidden on mobile */}
            <Col lg={3}>
              <div className="description-sticky" style={{ maxWidth: '90%' }}>
                <div className="subhead-black" style={{ textTransform: 'uppercase', paddingBottom: 0 }}>{m.client}</div>
                <div className="subhead-black" style={{ textTransform: 'none', paddingBottom: 0 }}>{m.projectName}</div>
                {m.articleLink && m.articleLink.length > 0 && (
                  <div className="subhead-black" style={{ paddingTop: '0.3em', paddingBottom: '2em', textTransform: 'none' }}>
                    <a href={m.articleLink}>View article</a>
                  </div>
                )}
                <div className="subhead-black" style={{ textTransform: 'none', paddingBottom: 0, paddingTop: (!m.articleLink || !m.articleLink.length) ? '2em' : 0 }}>
                  {m.discipline && m.discipline.split(',').map(d => `(${d.trim()})`).join(' ')}
                </div>
                {m.headline && m.headline.length > 0 && (
                  <div className="subhead-black" style={{ textTransform: 'none', paddingBottom: '2em' }}>{m.headline}</div>
                )}
                {/* Credits: visible on desktop only */}
                <div className="cs-credits-desktop">
                  <Credits m={m} />
                </div>
              </div>
            </Col>

            {/* Content — Col lg={6} */}
            <Col lg={6}>
              {m.splitScreen && m.shape && (
                <div className="featured-cs-image featured-cs-split" style={{ position: 'relative' }}>
                  <div className="featured-cs-split-left">
                    <Suspense fallback={<div style={{ width: '100%', height: '100%', background: '#787878' }} />}>
                      <Viewport3D shape={m.shape} hoverBg={m.rolloverColor} />
                    </Suspense>
                  </div>
                  <div className="featured-cs-split-right">
                    {m.imageUrl && <img src={m.imageUrl} alt={m.altDescription} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
                  </div>
                </div>
              )}
              {m.brief && (
                <div className={m.splitScreen ? 'cs-section-sep cs-brief' : 'cs-brief'}>
                  <div className="narrative-body">{m.brief}</div>
                </div>
              )}
              {m.sections && m.sections.map((section, i) => {
                const hasAbove = m.splitScreen || m.brief || m.coverImage || m.coverVideo;
                const s = (i === 0 && section.type === 'full' && !section.src && !m.splitScreen && m.imageUrl)
                  ? { ...section, src: m.imageUrl }
                  : section;
                return renderSection(s, (hasAbove || i > 0) ? i + 1 : 0);
              })}
              {m.outcome && (
                <div className="cs-section-sep">
                  <div className="narrative-label">Outcome</div>
                  <div className="narrative-body">{m.outcome}</div>
                </div>
              )}
              {/* Credits: visible on mobile only, after all content */}
              <div className="cs-credits-mobile cs-section-sep">
                <Credits m={m} />
              </div>
            </Col>

          </Row>
        </Container>
      </div>

      <ProjectIndex />
    </div>
  );
}

export default connect()(CaseStudyPage);
